import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Clickable } from "../../../../components/Clickable";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { TestIDs } from "../../../../tests/types/testIds";
import { Modal } from "../../../../components/modal/Modal";
import { ConversationMessage } from "../../../../model/integration/dto/meta/MetaConversationMessage";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { useAppSelector } from "../../../../redux/store";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { standardDate } from "../../../../utils/Date";
import { useWAMessageReaction } from "./useWAMessageReaction";
import { whatsappChatTitle } from "../../utils/whatsapp-chat-title";

const Row = styled(Flex)<{ $clickable: boolean }>`
  align-items: center;
  gap: ${Spacing.m};
  width: 100%;
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:hover {
    background-color: ${({ theme, $clickable }) =>
      $clickable ? theme.lightSub : "transparent"};
  }
`;

const Emoji = styled.span`
  font-size: 1.4rem;
  line-height: 1;
`;

type Props = {
  open: boolean;
  onClose: VoidFunction;
  message: ConversationMessage;
};

export const WAMessageReactionsModal: React.FC<Props> = ({
  open,
  onClose,
  message,
}) => {
  const { t } = useTranslation();
  const { ours, react } = useWAMessageReaction(message);

  const chat = useAppSelector((state) =>
    integrationSliceSelectors.selectWhatsAppMetaChatById(
      state,
      message.conversationId,
    ),
  );

  const reactions = message.reactions || [];

  const nameFor = (from: string) =>
    from === ours
      ? t("integration.whatsapp.meta.reply.you")
      : whatsappChatTitle(chat) || from;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("integration.whatsapp.meta.reactions.title")}
    >
      <Flex
        flexDirection="column"
        gap="xs"
        width="100%"
        dataTestId={
          TestIDs.integration.whatsappMeta.chat.reactionsModal.wrapper
        }
      >
        {reactions.length ? (
          reactions.map((reaction) => {
            const isOurs = reaction.from === ours;

            return (
              <Clickable
                key={`${reaction.from}-${reaction.emoji}`}
                onClick={isOurs ? () => react(reaction.emoji) : undefined}
                disabled={!isOurs}
                dataTestId={
                  isOurs
                    ? TestIDs.integration.whatsappMeta.chat.reactionsModal
                        .remove
                    : undefined
                }
              >
                <Row $clickable={isOurs}>
                  <Emoji>{reaction.emoji}</Emoji>

                  <Flex flexDirection="column" flex={1} minWidth="0">
                    <Text fontWeight="bold">{nameFor(reaction.from)}</Text>
                    {reaction.timestamp ? (
                      <Text fontSize="xs" color="darkerSub">
                        {standardDate<string>(
                          new Date(Number(reaction.timestamp) * 1000),
                          undefined,
                          true,
                        )}
                      </Text>
                    ) : null}
                  </Flex>

                  {isOurs ? (
                    <Text fontSize="s" color="danger">
                      {t("integration.whatsapp.meta.reactions.remove")}
                    </Text>
                  ) : null}
                </Row>
              </Clickable>
            );
          })
        ) : (
          <Text color="darkerSub">
            {t("integration.whatsapp.meta.reactions.empty")}
          </Text>
        )}
      </Flex>
    </Modal>
  );
};
